import { browser } from './browser';
import { UIModal } from '../components/modal/ui-modal';
import { waitForAnimations } from './helpers';
import { UIIFrameController } from '../components/iframe/ui-iframe-controller';
import { UIOverlayController } from '../components/overlay/ui-overlay-controller';
import type { UIModalController } from '../components/modal/ui-modal-controller';
import type { NullOr } from './utils.type';

type OverlayController = UIIFrameController | UIOverlayController;

// Callback payload types for overlay handlers
export interface MenuOverlayCallbackArgs<TController> {
    controller: TController;
    isDesktopMenu: boolean;
}

export interface ModalOverlayCallbackArgs<TController> {
    controller: TController;
    isNotBubble: boolean;
    isNavToggle: boolean;
    hasOverlay: boolean;
    blockScroll: boolean;
    partial: boolean;
}

export interface ResizeCallbackArgs<TController> {
    controller: TController;
    isMobileMediaMatches: boolean;
}

/**
 * @type {string[]}
 */
export const OverlayWatchEvents: Array<string> = [
    'modal-updatelist',
    'modal-open-enter',
    'modal-close-enter',
    'menu-open',
    'menu-close',
    'menu-toggle',
    'popover-updated',
];

/**
 * Edge case when ui-nav inside ui-navtoggle.
 * @param {EventTarget | HTMLElement} target
 * @returns {boolean}
 */
export const shouldMenuToggle = (
    target: EventTarget | HTMLElement | null
): boolean => {
    if (target instanceof HTMLElement) {
        return !target.closest('ui-modal,ui-navtoggle');
    }
    return false;
};

/**
 * @param {UIIFrameController | UIOverlayController} controller
 * @returns {boolean}
 */
export const isDesktopMenu = (
    controller: UIIFrameController | UIOverlayController
): boolean => {
    return !!(controller.mainMenu?.dropdown ?? browser.desktopMediaMatches());
};

/**
 * Edge case when tooltip is the last popover.
 * @param {UIIFrameController | UIOverlayController} controller
 * @returns {boolean}
 */
export const isTooltipLast = (
    controller: UIIFrameController | UIOverlayController
): boolean => {
    const modalController = controller.modalController as
        | UIModalController
        | undefined;
    const top = modalController?.getActiveModals().pop();
    return !!top?.classList.contains('-tooltip');
};

/**
 * Pickers that are anchored to a field must not use the full page iframe overlay.
 * Always skip: dropdown, autocomplete and anything with no-bottom-sheet (e.g. chip).
 * Datepicker is skipped only as type=menu (desktop). Mobile datepicker is a dialog.
 * Only a real mobile bottom-sheet (navtoggle) should notify the parent.
 */
export const isAnchoredMenuModal = (
    modal: NullOr<UIModal | HTMLElement>
): boolean => {
    if (!(modal instanceof HTMLElement)) {
        return false;
    }
    const el = modal.closest('ui-modal');
    if (!el) {
        return false;
    }

    const isDropdown = el.classList.contains('-dropdown');
    const isAutocomplete = el.classList.contains('-autocomplete');
    const optedOutOfSheet = el.hasAttribute('no-bottom-sheet');

    /* Always anchored */
    if (isDropdown || isAutocomplete || optedOutOfSheet) {
        return true;
    }
    /* Not of type menu -> not anchored */
    const isMenu = el.getAttribute('type') === UIModal.Type.Menu;
    if (!isMenu) {
        return false;
    }
    /* Anchored only when type is menu */
    const isDatepicker = el.classList.contains('-datepicker');
    if (isDatepicker) {
        return true;
    }

    return !(browser.mobileMediaMatches() && el.hasAttribute('bottom-sheet'));
};

/**
 * @param {UIModalController} modalController
 * @returns {boolean}
 */
export const hasLastOverlay = (modalController: UIModalController): boolean => {
    const predicate = (modal: UIModal) =>
        modal.classList.contains('-presented') && !isAnchoredMenuModal(modal);
    return !!modalController.getActiveModals().find(predicate);
};

/**
 * Handles menu overlay open/close events.
 */
export const mainMenuOverlayHandler = <T extends OverlayController>(
    controller: T,
    event: CustomEvent | Event,
    openCallback: (args: MenuOverlayCallbackArgs<T>) => void,
    closeCallback: (args: MenuOverlayCallbackArgs<T>) => void
): void => {
    const isMenuOnDesktop = isDesktopMenu(controller);
    switch (event.type) {
        case 'menu-open':
            if (
                !shouldMenuToggle(event.target) ||
                (controller.menuOpened && isMenuOnDesktop)
            ) {
                break;
            }
            openCallback({
                controller,
                isDesktopMenu: isMenuOnDesktop,
            });
            break;
        case 'menu-close':
            if (!shouldMenuToggle(event.target)) {
                break;
            }
            closeCallback({
                controller,
                isDesktopMenu: isMenuOnDesktop,
            });
            break;
    }
};

const resolveOverlayModal = (event: Event): UIModal | null => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
        return null;
    }
    if (target.localName === 'ui-modal') {
        return target as UIModal;
    }
    return target.closest('ui-modal');
};

/**
 * Handles modal overlay enter/exit/update events.
 */
export const modalOverlayHandler = async <T extends OverlayController>(
    controller: T,
    event: CustomEvent | Event,
    enterCallback: (args: ModalOverlayCallbackArgs<T>) => void,
    exitCallback: (args: ModalOverlayCallbackArgs<T>) => void,
    updateCallback: (args: ModalOverlayCallbackArgs<T>) => void
): Promise<void> => {
    // Normal (page) modals use event 'modal-open-enter' and 'modal-close-enter'.
    // All popover-like use mostly 'modal-updatelist'.
    const modal = resolveOverlayModal(event);
    if (!modal) {
        return;
    }
    const isPopover = modal.type === UIModal.Type.Popover;
    const isFixedAlignment = modal.align === 'fixed';
    const isAnchoredMenu = isAnchoredMenuModal(modal);
    let modalAction = '';
    if (event instanceof CustomEvent && event.detail) {
        modalAction = String(event.detail?.action);
    }
    const isRemovedAction = modalAction === 'remove';
    const isAddedAction = modalAction === 'add';
    const isNavToggle = modal.classList.contains('-navtoggle');
    const isNotBubble = modal.type !== UIModal.Type.Bubble;

    try {
        await waitForAnimations(modal);
    } catch (error) {
        // animation interrupted, do nothing
    }
    const modalController = controller.modalController as
        | UIModalController
        | undefined;
    const hasLastOverlayValue = modalController
        ? hasLastOverlay(modalController)
        : false;

    const cbPayload = {
        controller,
        isNotBubble,
        isNavToggle: false,
        hasOverlay: hasLastOverlayValue,
        blockScroll: hasLastOverlayValue,
        partial: false,
    };

    switch (event.type) {
        case 'modal-open-enter':
            if (!isPopover && !isFixedAlignment && !isAnchoredMenu) {
                enterCallback(cbPayload);
            }
            break;
        case 'modal-close-enter': {
            if (!isPopover && !isFixedAlignment && !isAnchoredMenu) {
                exitCallback(cbPayload);
            }
            break;
        }
        case 'modal-updatelist':
            if (isPopover && isFixedAlignment) {
                const hasOverlay =
                    isAddedAction || !(isRemovedAction || isNavToggle);
                const blockScroll = isAddedAction || hasLastOverlayValue;
                updateCallback({ ...cbPayload, hasOverlay, blockScroll });
            }
            break;
    }
};

/**
 * Handles menu resize events.
 */
export const handleMenuOnResize = <T extends OverlayController>(
    controller: T,
    event: MediaQueryListEvent | Event,
    callback: (args: ResizeCallbackArgs<T>) => void
): void => {
    if (isTooltipLast(controller)) {
        return;
    }
    if (!('matches' in event)) {
        return;
    }
    // Really weird and complex menu/navtoggle logic.
    const isMobileMediaMatches = event.matches;
    const cbPayload = {
        controller,
        isMobileMediaMatches,
    };
    callback(cbPayload);
};

/**
 * Handles tooltip resize events.
 */
export const handleTooltipsOnResize = <T extends OverlayController>(
    controller: T,
    event: MediaQueryListEvent | Event,
    callback: (args: ResizeCallbackArgs<T>) => void
): void => {
    if (!isTooltipLast(controller)) {
        return;
    }
    if (!('matches' in event)) {
        return;
    }
    const isMobileMediaMatches = event.matches;
    const cbPayload = {
        controller,
        isMobileMediaMatches,
    };
    callback(cbPayload);
};
